import React, { useEffect } from 'react';
import { XCircle } from 'lucide-react';
import useFeedbackStore from './FeedbackStore';
import './ContextualMessageOverlay.css';

/**
 * Overlay component that renders contextual floating messages
 * Messages appear at the last click position and animate upward while fading out
 */
function ContextualMessageOverlay() {
  const contextualMessages = useFeedbackStore((state) => state.contextualMessages);
  const initClickListener = useFeedbackStore((state) => state.initClickListener);

  // Initialize click position tracking on mount
  useEffect(() => {
    initClickListener();
  }, [initClickListener]);

  if (contextualMessages.length === 0) {
    return null;
  }

  return (
    <div className="contextual-message-overlay">
      {contextualMessages.map((msg, index) => (
        <ContextualMessage
          key={msg.id}
          message={msg}
          stackIndex={index}
        />
      ))}
    </div>
  );
}

/**
 * Individual contextual message component
 * Handles positioning and animation for a single message
 */
function ContextualMessage({ message, stackIndex }) {
  const { x, y, message: text, duration, customClass, useCustomClass, type } = message;

  // Calculate position - stack messages downward (newer messages below older ones)
  // Each message is offset 35px down from the previous
  const stackOffset = stackIndex * 35;

  // Z-index increases with stack index so newer messages appear in front
  const zIndex = 9999 + stackIndex;

  // For custom class positioning, find the element and position below it
  let positionStyle = {};

  if (useCustomClass && customClass) {
    const targetElement = document.querySelector(`.${customClass}`);
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      positionStyle = {
        left: rect.left + rect.width / 2,
        top: rect.bottom + 10 + stackOffset, // Stack downward from element
        transform: 'translateX(-50%)',
        zIndex
      };
    } else {
      // Fallback to click position if element not found
      positionStyle = {
        left: x,
        top: y + stackOffset,
        transform: 'translate(-50%, -100%)',
        zIndex
      };
    }
  } else {
    // Default: position at click coordinates, stack downward
    positionStyle = {
      left: x,
      top: y + stackOffset,
      transform: 'translate(-50%, -100%)',
      zIndex
    };
  }

  return (
    <div
      className={`contextual-message ${type === 'error' ? 'contextual-message-error' : ''}`}
      style={{
        ...positionStyle,
        '--animation-duration': `${duration}ms`
      }}
    >
      {type === 'error' && (
        <XCircle className="contextual-message-icon" size={18} />
      )}
      <span className="contextual-message-text">{text}</span>
    </div>
  );
}

export default React.memo(ContextualMessageOverlay);
